import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import "./style.css";
import { getTotalData } from "../../../services/dashboardService";
import { toastAlert } from "../../../helpers/toastAlert";
import { canAssignStaff, hasPermission } from "../../../utils/commonutils";

const TotalDatainfo = (props) => {
  const { kpiOrder = [], hiddenKpis = [] } = props;
  const [totalData, setTotalData] = useState({
    customerTotal: 0,
    employeeTotal: 0,
    taskTotal: 0,
    roleTotal: 0,
    inquiryTotal: 0,
    paymentRecoveryTotal: 0,
    caseTotal: 0,
    communicationTotal: 0,
    followupToday: 0,
    followupOverdue: 0,
    followupUpcoming: 0,
    scope: "firm",
    role: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await getTotalData(props);
        const data = response?.data;
        if (data) {
          setTotalData({
            customerTotal: data.customerTotal || 0,
            employeeTotal: data.employeeTotal || 0,
            taskTotal: data.taskTotal || 0,
            roleTotal: data.roleTotal || 0,
            inquiryTotal: data.inquiryTotal || 0,
            paymentRecoveryTotal: data.paymentRecoveryTotal || 0,
            caseTotal: data.caseTotal || 0,
            communicationTotal: data.communicationTotal || 0,
            followupToday: data.followupToday || 0,
            followupOverdue: data.followupOverdue || 0,
            followupUpcoming: data.followupUpcoming || 0,
            scope: data.scope,
            role: data.role,
          });
        } else {
          toastAlert(response?.message || "Problem Fetching Records.", "error");
        }
      } catch (error) {
        toastAlert("Please check Internet Connection.", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const goTo = (path) => {
    if (!path) return;
    if (path.startsWith("#")) {
      document.querySelector(path)?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    props.history.push(path);
  };

  const role = totalData?.role || props.authData?.role || "";
  const isMine = totalData?.scope === "mine";
  const isLead = canAssignStaff(role);
  const followupsPath = "#dashboard-followups";

  const cards = useMemo(() => {
    const all = [
      (hasPermission("read_followup") || hasPermission("read_inquiry")) && {
        id: "followupToday",
        label: isMine ? "My follow-ups today" : "Follow-ups today",
        value: totalData?.followupToday ?? 0,
        hint: "Open board",
        path: followupsPath,
      },
      (hasPermission("read_followup") || hasPermission("read_inquiry")) && {
        id: "followupOverdue",
        label: "Overdue",
        value: totalData?.followupOverdue ?? 0,
        hint: "Needs attention",
        danger: true,
        path: followupsPath,
      },
      (hasPermission("read_followup") || hasPermission("read_inquiry")) && {
        id: "followupUpcoming",
        label: "Upcoming",
        value: totalData?.followupUpcoming ?? 0,
        hint: "Next 30 days",
        path: followupsPath,
      },
      hasPermission("read_inquiry") && {
        id: "inquiries",
        label: isMine ? "My inquiries" : "Inquiries",
        value: totalData?.inquiryTotal ?? 0,
        hint: "Open list",
        path: "/inquiary",
      },
      hasPermission("read_task") && {
        id: "tasks",
        label: isMine ? "My tasks" : "Tasks",
        value: totalData?.taskTotal ?? 0,
        hint: "Open board",
        path: "/tasks",
      },
      hasPermission("read_paymentrecovery") && {
        id: "payment",
        label: isMine ? "My recoveries" : "Payment recovery",
        value: totalData?.paymentRecoveryTotal ?? 0,
        hint: "Open files",
        path: "/payment",
      },
      hasPermission("read_casemanagement") && {
        id: "cases",
        label: isMine ? "My GST cases" : "GST cases",
        value: totalData?.caseTotal ?? 0,
        hint: "Open cases",
        path: "/casemanagement",
      },
      hasPermission("read_customers") && {
        id: "customers",
        label: "Customers",
        value: totalData?.customerTotal ?? 0,
        hint: "Open list",
        path: "/customers",
      },
      hasPermission("read_communication") && {
        id: "communications",
        label: isMine ? "My communications" : "Communications",
        value: totalData?.communicationTotal ?? 0,
        hint: "Open log",
        path: "/communication",
      },
      isLead && hasPermission("read_employee") && {
        id: "employees",
        label: "Employees",
        value: totalData?.employeeTotal ?? 0,
        hint: "Team",
        path: "/employees",
      },
      isLead && hasPermission("read_role") && {
        id: "roles",
        label: "Roles",
        value: totalData?.roleTotal ?? 0,
        hint: "Access",
        path: "/roles",
      },
    ].filter(Boolean);

    const rank = new Map((kpiOrder || []).map((id, index) => [id, index]));
    all.sort((a, b) => (rank.has(a.id) ? rank.get(a.id) : 999) - (rank.has(b.id) ? rank.get(b.id) : 999));
    return all.filter((item) => !hiddenKpis.includes(item.id));
  }, [totalData, isMine, isLead, kpiOrder, hiddenKpis]);

  if (loading) {
    return <CircularProgress style={{ color: "#1a3366" }} size={40} />;
  }

  return (
    <div className="kpi-strip">
      {cards.map((item) => (
        <div
          className="kpi-card"
          key={item.id}
          onClick={() => goTo(item.path)}
          style={{ cursor: "pointer", borderColor: item.danger && item.value ? "#f0c7c2" : undefined }}
        >
          <div
            style={{
              color: item.danger && item.value ? "#b42318" : "#64748b",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            {item.label}
          </div>
          <h3 style={{ color: item.danger && item.value ? "#b42318" : "#0f172a" }}>{item.value}</h3>
          <div style={{ color: "#94a3b8", fontSize: 12 }}>{item.hint}</div>
        </div>
      ))}
    </div>
  );
};

const mapStateToProps = (state) => ({
  authData: state.authReducer,
});

export default withRouter(connect(mapStateToProps)(TotalDatainfo));
